import * as types from '../../actions/actionTypes';
import initialState from '../initialState';
import { combineReducers } from 'redux';
import insuranceReducer from 'features/offer/edit/financials/insurance/reducer';
import offerAssetReducer from 'features/offer/edit/assets/asset/reducers/offerAssetReducer';
import leasingExpensesReducer from 'features/offer/edit/financials/leasingExpenses/reducer';
import financialDataSummaryReducer from 'features/offer/edit/financials/financialDataSummary/reducer';
import workflowReducer from './workflow/workflowReducer';
import financingStructureReducer from './financials/financingStructureReducer';
import financialServicesReducer from 'features/offer/edit/financials/financialServices/reducer';
import assetClientReducer from 'features/offer/edit/assets/client/reducer';
import clientMonthlyObligationsReducer from 'features/offer/edit/client/monthlyObligations/reducer';
import clientActivityFieldsReducer from 'features/offer/edit/client/activities/reducer';
import partnerEligibilityReducer from 'features/offer/edit/client/eligibility/reducer';
import partnerKnockoutCriteriaReducer from 'features/offer/edit/client/knockoutCriteria/reducer';
import contactPersonsReducer from 'features/offer/edit/client/contactPersons/reducer';
import contactModesReducer from 'features/offer/edit/client/contactModes/reducer';
import clientPropertiesReducer from 'features/offer/edit/client/properties/reducer';
import clientValidationReducer from 'features/offer/edit/client/verification/reducer';
import consentReducer from 'features/offer/edit/client/consent/reducer';
import bankAccountReducer from 'features/offer/edit/client/bankAccount/reducer';
import shareholdersReducer from 'features/offer/edit/client/shareholders/reducer';
import incomeTypesReducer from 'features/offer/edit/client/income/reducers/incomeTypesReducer';
import incomeDataReducer from 'features/offer/edit/client/income/reducers/incomeDataReducer';
import clientDataReducer from 'features/offer/edit/client/form/reducers/offerClientDataReducer';
import addressesReducer from 'features/offer/edit/client/form/reducers/addressesReducer';
import financialFeeReducer from 'features/offer/edit/financials/fee/reducer';
import commissionsReducer from 'features/offer/edit/financials/commissions/reducer';
import calculationMethodsReducer from 'features/offer/edit/financials/commissions/calculationMethodsReducer';
import paymentPlanReducer from 'features/offer/edit/financials/paymentPlan/reducers/reducer';
import advanceSplitReducer from 'features/offer/edit/financials/paymentPlan/reducers/advanceSplitReducer';
import generalDetailsReducer from 'features/offer/edit/assets/generalDetails/reducer';
import partnerRelationsReducer from 'reducers/currentOffer/partnerRelations/partnerRelationsReducer';
import {
  OFFER_PARTNER_UPDATED,
  ASSOCIATED_PARTNERS_INVALIDATED,
  SAVE_ADDED_HIERARCHY_PARTNERS_UPDATED,
  DOCUMENT_PARTNERS_INVALIDATED
} from 'features/offer/edit/client/form/eventTypes';
import { LOAD_OFFER_ASSOCIATED_PARTNERS_SUCCEEDED } from 'features/offer/edit/client/form/actionTypes';
import { OFFER_FINANCIAL_PRODUCT_LIST_INVALIDATED } from 'actions/eventTypes/offer/financials/offerFinancialProduct';
import { OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_INVALIDATED } from 'actions/eventTypes';
import { OFFER_ORCHESTRATOR_ENABLED } from 'actions/eventTypes';
import sidebarReducer from 'features/offer/edit/sidebar/reducer';
import { OFFER_ASSET_CLIENT_UPDATED } from 'features/offer/edit/assets/client/eventTypes';
import productSelectionReducer from 'features/offer/edit/productSelection/reducer';
import questionnaireReducer from 'features/offer/edit/client/questionnaire/reducer';
import { minimumNaturalNumber } from 'constants/numericLimits';
import { emptyArray } from 'constants/emptyConst';
import { CLIENT } from 'constants/partnerRolesCode';

function legacyCurrentOfferReducer(state = initialState.currentOffer, action) {
  switch (action.type) {
    case types.RESET_CURRENT_OFFER:
      // eslint-disable-next-line
      if (action.payload.documentId == state.header.documentId && action.payload.siteId == state.header.siteId) {
        return initialState.currentOffer;
      }
      break;

    case types.OFFER_ORCHESTRATOR_CURRENT_OFFER_INVALIDATED: {
      return initialState.currentOffer;
    }

    case types.OFFER_ORCHESTRATOR_FOLDER_GENERATED: {
      let folderId = state.header.folderId;
      if (action.event.eventBody.partnerRoleCode === CLIENT) folderId = action.event.eventBody.folderId;

      return {
        ...state,
        header: {
          ...state.header,
          folderId
        }
      };
    }

    case types.OFFER_ORCHESTRATOR_FACILITY_FOLDER_GENERATED: {
      let folderId = state.header.folderId;
      folderId = folderId ?? action.event.eventBody.folderId;
      return {
        ...state,
        header: {
          ...state.header,
          folderId
        }
      };
    }

    case types.LOAD_OFFER_HEADER_STARTED:
      return {
        ...state,
        header: {
          ...state.header,
          loading: true
        }
      };

    case types.OFFER_STATE_UPDATED: {
      return {
        ...state,
        nextStates: initialState.currentOffer.nextStates,
        header: {
          ...state.header,
          stateId: action.event.eventBody.stateId,
          stateName: action.event.eventBody.stateName,
          folderSelectionDone: null,
          folderList: null
        }
      };
    }

    case types.LOAD_OFFER_HEADER_SUCCESS: {
      const { financialProductId, financialProductName, activeStep, ...rest } = action.offer;
      const isActiveStepValid = Boolean(activeStep && !isNaN(activeStep));
      return {
        ...state,
        header: {
          ...rest,
          activeStep: isActiveStepValid ? parseInt(activeStep) : minimumNaturalNumber,
          financialProductId,
          financialProductName,
          loading: false
        },
        productSelectionData: {
          ...state.productSelectionData,
          products: [...(state.productSelectionData.products || emptyArray)],
          financialProductId,
          financialProductName
        }
      };
    }

    case OFFER_FINANCIAL_PRODUCT_LIST_INVALIDATED: {
      return {
        ...state,
        productSelectionData: initialState.productSelectionData
      };
    }

    case types.OFFER_REPORTS_EXECUTED: {
      return {
        ...state,
        offerReports: Object.assign([], action.event.eventBody.list, { isLoaded: true })
      };
    }

    case types.LOAD_OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_SUCCESS: {
      return {
        ...state,
        offerFinancialProductRuleReadOnlyFields: Object.assign({}, action.offerFinancialProductRuleReadOnlyFields, {
          isLoaded: true,
          loading: false
        })
      };
    }

    case types.LOAD_OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_STARTED: {
      return {
        ...state,
        offerFinancialProductRuleReadOnlyFields: Object.assign({}, state.offerFinancialProductRuleReadOnlyFields, {
          loading: true
        })
      };
    }

    case OFFER_FINANCIAL_PRODUCT_RULE_READONLY_FIELDS_INVALIDATED: {
      return {
        ...state,
        offerFinancialProductRuleReadOnlyFields: initialState.currentOffer.offerFinancialProductRuleReadOnlyFields
      };
    }

    case OFFER_ORCHESTRATOR_ENABLED: {
      return {
        ...state,
        header: {
          ...state.header,
          processing: false
        }
      };
    }

    case types.OFFER_NEXT_STATES_LIST_COMPUTED: {
      return {
        ...state,
        nextStates: {
          ...state.nextStates,
          nextStatesList: [...action.event.eventBody.nextStates],
          isLoaded: true
        }
      };
    }

    case types.LOAD_OFFER_DECISION_SELECTED_CONDITIONS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          selectedConditions: Object.assign(action.payload, { isLoaded: true })
        }
      };

    case types.LOAD_OFFER_DECISION_AVAILABLE_CONDITIONS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          availableConditions: Object.assign(action.payload, { isLoaded: true })
        }
      };

    case types.LOAD_OFFER_DECISION_FINANCED_ASSETS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          financedAssets: Object.assign([], action.payload, { isLoaded: true })
        }
      };

    case types.OFFER_DECISION_FINANCED_ASSET_INVALIDATED:
      return {
        ...state,
        decision: {
          ...state.decision,
          financedAssets: initialState.currentOffer.decision.financedAssets
        }
      };

    case types.UPDATE_OFFER_DECISION_SELECTED_CONDITIONS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          selectedConditions: Object.assign(action.payload, { isLoaded: true })
        }
      };

    case types.LOAD_OFFER_DECISION_SUBSEQUENT_CONDITIONS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          subsequentConditions: Object.assign(action.payload, { isLoaded: true })
        }
      };

    case types.UPDATE_OFFER_DECISION_SUBSEQUENT_CONDITIONS_SUCCESS:
      return {
        ...state,
        decision: {
          ...state.decision,
          subsequentConditions: Object.assign(action.payload, { isLoaded: true })
        }
      };

    case types.OFFER_DECISION_SUBSEQUENT_CONDITION_OVERDUE: {
      return {
        ...state,
        decision: {
          ...state.decision,
          subsequentConditions: initialState.currentOffer.decision.subsequentConditions
        }
      };
    }

    case types.LOAD_OFFER_WARNINGS_SUCCESS: {
      return {
        ...state,
        warnings: Object.assign([], action.payload.documentWarnings, { isValid: true, loaded: true })
      };
    }

    case types.OFFER_WARNINGS_CHANGED: {
      return {
        ...state,
        warnings: Object.assign([], { isValid: false })
      };
    }

    case types.DOCUMENT_WORKFLOW_ERROR_DISMISSED: {
      return {
        ...state,
        warnings: Object.assign(
          [],
          state.warnings.filter(x => x.documentWorkflowErrorId !== action.event.eventBody.documentWorkflowErrorId),
          { isValid: true, loaded: true }
        )
      };
    }

    case types.LOAD_OFFER_SOURCES_SUCCESS:
      return {
        ...state,
        sources: Object.assign([], action.payload, { isLoaded: true })
      };

    case OFFER_ASSET_CLIENT_UPDATED: {
      const { clientId, clientName, partnerTypeCode, isPF, isPJ, isPFA, partnerGroupId } = action.event.eventBody;
      return {
        ...state,
        header: {
          ...state.header,
          client: {
            clientId,
            clientName,
            partnerTypeCode,
            isPF,
            isPJ,
            isPFA
          },
          partnerGroupId: partnerGroupId
        }
      };
    }

    case OFFER_PARTNER_UPDATED: {
      const { partnerId, partnerName, partnerTypeCode, isAssociatedPartner, isPF, isPJ, isPFA, partnerGroupId } = action.event.eventBody;
      if (isAssociatedPartner) {
        return {
          ...state,
          header: {
            ...state.header,
            lastChangedAssociatedPartnerId: partnerId
          }
        };
      } else {
        return {
          ...state,
          header: {
            ...state.header,
            client: {
              clientId: partnerId,
              clientName: partnerName,
              partnerTypeCode: partnerTypeCode,
              isPF: isPF,
              isPJ: isPJ,
              isPFA: isPFA
            },
            partnerGroupId: partnerGroupId
          }
        };
      }
    }

    case SAVE_ADDED_HIERARCHY_PARTNERS_UPDATED: {
      const { addedPartners } = action.event.eventBody;
      const index = addedPartners.length - 1;
      let lastChangedAssociatedPartnerId;
      lastChangedAssociatedPartnerId = addedPartners[index].partnerId;
      if (index > 0) {
        return {
          ...state,
          lastChangedAssociatedPartnerId: lastChangedAssociatedPartnerId
        };
      }
      break;
    }

    case ASSOCIATED_PARTNERS_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const associatedPartners = {
        ...state.associatedPartners,
        [key]: {
          loaded: false
        }
      };
      return {
        ...state,
        associatedPartners
      };
    }
    case DOCUMENT_PARTNERS_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const documentPartners = {
        ...state.documentPartners,
        [key]: {
          loaded: false
        }
      };
      return {
        ...state,
        documentPartners
      };
    }

    case LOAD_OFFER_ASSOCIATED_PARTNERS_SUCCEEDED: {
      const { key } = action;
      const associatedPartners = {
        ...state.associatedPartners,
        [key]: {
          loading: false,
          loaded: true
        }
      };

      return {
        ...state,
        associatedPartners
      };
    }

    case types.FOLDER_LIST_COMPUTED: {
      return {
        ...state,
        header: {
          ...state.header,
          folderList: action.event.eventBody.folders
        }
      };
    }

    case types.OFFER_FOLDER_UPDATED: {
      return {
        ...state,
        header: {
          ...state.header,
          folderSelectionDone: true,
          folderList: []
        }
      };
    }

    case types.RESET_NEXT_STATE_FOLDER_LIST: {
      return {
        ...state,
        header: {
          ...state.header,
          folderSelectionDone: null,
          folderList: null
        }
      };
    }

    case types.SIDEBAR_DRAWER_SELECTED: {
      if (state.header.documentId == null) return state;
      return {
        ...state,
        offerSidebarData: { ...state.offerSidebarData, sidebarDrawer: action.payload }
      };
    }

    default:
      return state;
  }

  return state;
}
const passThrough = (state = {}) => state;
const combinedSlicesReducer = combineReducers({
  assetData: offerAssetReducer,
  assetClientData: assetClientReducer,
  clientMonthlyObligationsData: clientMonthlyObligationsReducer,
  clientActivityFieldsData: clientActivityFieldsReducer,
  partnerEligibilityData: partnerEligibilityReducer,
  partnerKnockoutCriteria: partnerKnockoutCriteriaReducer,
  clientContactPersonsData: contactPersonsReducer,
  clientContactModesData: contactModesReducer,
  clientShareholdersData: shareholdersReducer,
  clientPropertiesData: clientPropertiesReducer,
  clientIncomeTypesData: incomeTypesReducer,
  clientIncomeData: incomeDataReducer,
  clientVerificationsData: clientValidationReducer,
  clientConsentData: consentReducer,
  clientBankAccountData: bankAccountReducer,
  clientData: clientDataReducer,
  insuranceData: insuranceReducer,
  workflows: workflowReducer,
  financingStructureData: financingStructureReducer,
  financialServicesData: financialServicesReducer,
  feesData: financialFeeReducer,
  commissions: commissionsReducer,
  calculationMethods: calculationMethodsReducer,
  leasingExpenses: leasingExpensesReducer,
  financialDataSummary: financialDataSummaryReducer,
  paymentPlanData: paymentPlanReducer,
  clientAddressesData: addressesReducer,
  advanceSplitData: advanceSplitReducer,
  header: generalDetailsReducer,
  offerSidebarData: sidebarReducer,
  productSelectionData: productSelectionReducer,
  clientQuestionnaireData: questionnaireReducer,
  documentPartnerRelations: partnerRelationsReducer,
  //--------------------------
  //Not in use
  //--------------------------
  nextStates: passThrough,
  offerFinancialProductRuleReadOnlyFields: passThrough,
  sources: passThrough,
  offerPartners: passThrough,
  associatedPartners: passThrough,
  uploadDocuments: passThrough,
  availableUploadDocuments: passThrough,
  exportDocuments: passThrough,
  warnings: passThrough,
  decision: passThrough,
  offerReports: passThrough
  //--------------------------
});

export default function currentOfferReducer(state = initialState.currentOffer, action) {
  const intermediateState = legacyCurrentOfferReducer(state, action);
  const finalState = combinedSlicesReducer(intermediateState, action);
  return { ...intermediateState, ...finalState };
}
